import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { database } from '../../firebase';
import styles from './CallRecordingPlayer.module.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const SPEEDS = [1, 1.25, 1.5, 1.75, 2];

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (sec) => {
  if (!isFinite(sec) || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ── ChunkPlayer ───────────────────────────────────────────────────────────────
const ChunkPlayer = ({ chunk, chunkKey }) => {
  const audioRef = useRef(null);

  const [speed,       setSpeed]       = useState(1);
  const [corrupt,     setCorrupt]     = useState(false);
  const [loading,     setLoading]     = useState(true);   // buffering via browser
  const [playing,     setPlaying]     = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]    = useState(0);

  // On mount: set src and trigger play→pause so the browser starts buffering
  // the full file natively (avoids any CORS fetch issue).
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.src = chunk.url;
    audio.load();

    // play() then immediately pause — this kicks off buffering without
    // actually letting any audio out to the speaker.
    const trigger = audio.play();
    if (trigger && typeof trigger.then === 'function') {
      trigger.then(() => audio.pause()).catch(() => {
        // Autoplay blocked — audio.load() above is still buffering in the
        // background; canplaythrough will fire when ready.
      });
    } else {
      audio.pause();
    }

    return () => {
      audio.pause();
      audio.src = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chunk.url]);

  // ── audio event handlers ──────────────────────────────────────────────────
  const handleCanPlayThrough = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isFinite(audio.duration) && audio.duration > 0) {
      // Duration already known — show the player immediately
      setDuration(audio.duration);
      setLoading(false);
    } else {
      // WebM files often lack duration metadata; seek to end to force the
      // browser to scan the whole file and report the real duration via
      // durationchange, then snap back to 0.
      audio.currentTime = 1e101;
    }
  };

  const handleDurationChange = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isFinite(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration);
      // Snap back to start after the forced seek-to-end
      if (audio.currentTime > 0 && !playing) audio.currentTime = 0;
      setLoading(false);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };

  const handleEnded = () => setPlaying(false);

  const handleAudioError = () => {
    console.warn('CallRecordingPlayer: corrupt or unplayable chunk', chunkKey, chunk.url);
    setCorrupt(true);
    setLoading(false);
  };

  // ── control handlers ──────────────────────────────────────────────────────
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.playbackRate = speed;
      audio.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const val = parseFloat(e.target.value);
    audio.currentTime = val;
    setCurrentTime(val);
  };

  const handleSpeedChange = (s) => {
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };

  if (corrupt) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={styles.chunkRow}>
      {/* Meta */}
      <div className={styles.chunkMeta}>
        <span className={styles.chunkLabel}>Chunk {chunkKey}</span>
        {chunk.timestamp && (
          <span className={styles.chunkTime}>
            {moment(chunk.timestamp).format('MMM DD YYYY, HH:mm:ss')}
          </span>
        )}
      </div>

      {/* Hidden audio element — we drive it manually */}
      <audio
        ref={audioRef}
        preload="auto"
        onCanPlayThrough={handleCanPlayThrough}
        onDurationChange={handleDurationChange}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={handleAudioError}
      />

      {/* Custom player */}
      <div className={styles.playerCell}>
        {loading ? (
          <div className={styles.bufferingBar}>
            Buffering audio…
          </div>
        ) : (
          <>
            {/* Row 1: play button + seek bar + time */}
            <div className={styles.playerControls}>
              <button
                className={styles.playBtn}
                onClick={togglePlay}
                aria-label={playing ? 'Pause' : 'Play'}
              >
                {playing ? '⏸' : '▶'}
              </button>

              <input
                type="range"
                className={styles.seekBar}
                min={0}
                max={duration || 0}
                step={0.1}
                value={currentTime}
                onChange={handleSeek}
              />

              <span className={styles.timeDisplay}>
                {fmt(currentTime)} / {fmt(duration)}
              </span>
            </div>

            {/* Row 2: speed controls */}
            <div className={styles.speedGroup}>
              <span style={{ fontSize: 11, color: '#888', marginRight: 4 }}>Speed:</span>
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSpeedChange(s)}
                  className={`${styles.speedBtn} ${speed === s ? styles.speedBtnActive : ''}`}
                >
                  {s}x
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── CallRecordingPlayer ───────────────────────────────────────────────────────
/**
 * Reusable inline call-recording player.
 *
 * Props:
 *   sessionID  {string}  – Firebase conversation ID  (e.g. props.sessionID)
 *   messageKey {string}  – Firebase message key      (e.g. props.messageKey)
 *                          When provided, only that single message's recordings
 *                          are shown.  Omit to show ALL recordings for sessionID.
 *   label      {string}  – Optional section header label (default "Call Recording")
 */
const CallRecordingPlayer = ({ sessionID, messageKey, label = 'Call Recording' }) => {
  const [messages, setMessages] = useState({});
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (!sessionID) return;

    setLoading(true);

    const path = messageKey
      ? `/mentalHealthTranscript/${sessionID}/${messageKey}`
      : `/mentalHealthTranscript/${sessionID}`;

    database
      .ref(path)
      .once('value')
      .then((snap) => {
        const val = snap.val();
        if (!val) {
          setMessages({});
          return;
        }
        // When fetching a single message the result is the message object itself
        // (not a map of messages).  Normalise to { [messageKey]: messageObj }.
        if (messageKey) {
          setMessages({ [messageKey]: val });
        } else {
          setMessages(val);
        }
      })
      .catch((err) => {
        console.error('CallRecordingPlayer: failed to fetch recordings', err);
        setMessages({});
      })
      .finally(() => setLoading(false));
  }, [sessionID, messageKey]);

  const messageKeys = Object.keys(messages);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.headerIcon}>🎙️</span>
        {label}
      </div>

      {loading && (
        <p className={styles.loading}>Loading recordings…</p>
      )}

      {!loading && messageKeys.length === 0 && (
        <p className={styles.empty}>No call recordings found.</p>
      )}

      {!loading && messageKeys.map((msgID, idx) => {
        const msg       = messages[msgID] || {};
        const chunks    = msg.chunks || {};
        // Only keep chunks that have a non-empty url
        const chunkKeys = Object.keys(chunks)
          .sort()
          .filter((ck) => typeof chunks[ck]?.url === 'string' && chunks[ck].url.trim() !== '');
        const msgTime   = msg.aiProcessingTimestamp || msg.timestamp;

        return (
          <div key={msgID} className={styles.callBlock}>
            {/* Call header */}
            <div className={styles.callBlockHeader}>
              <span>
                Call #{idx + 1}
                {msg.callType ? ` · ${msg.callType}` : ''}
                <span className={styles.callBlockHeaderId}>{msgID}</span>
              </span>
              {msgTime && (
                <span className={styles.callBlockHeaderTime}>
                  {moment(msgTime).format('MMM DD YYYY, HH:mm')}
                </span>
              )}
            </div>

            {/* Chunks */}
            {chunkKeys.length === 0 ? (
              <div className={styles.chunkRow}>
                <span className={styles.empty}>No audio chunks available.</span>
              </div>
            ) : (
              chunkKeys.map((ck) => (
                <ChunkPlayer key={ck} chunkKey={ck} chunk={chunks[ck]} />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CallRecordingPlayer;
